import { DateTime } from 'luxon';
import { CreditActivityResponse } from 'lib/types/credits';
import { MobileTooltip } from 'components/MobileTooltip';

interface DayCell {
  date: DateTime;
  dateKey: string;
  count: number;
  inRange: boolean;
}

interface Props {
  activity?: CreditActivityResponse;
}

const levelClasses = [
  'bg-hintgray',
  'bg-palegreen',
  'bg-sky',
  'bg-teal',
  'bg-alpineTeal',
];

const getLevel = (count: number, maxCount: number) => {
  if (count <= 0 || maxCount <= 0) {
    return 0;
  }
  const step = maxCount / 4;
  return Math.min(4, Math.max(1, Math.ceil(count / step)));
};

export const CreditContributions = ({ activity }: Props) => {
  const endDate = activity?.to
    ? DateTime.fromISO(activity.to, { zone: 'utc' }).startOf('day')
    : DateTime.utc().startOf('day');
  const startDate = activity?.from
    ? DateTime.fromISO(activity.from, { zone: 'utc' }).startOf('day')
    : endDate.minus({ days: 364 });

  const countsByDate = new Map(
    (activity?.days || []).map((day) => [day.date, day.count]),
  );

  const leadingDays = startDate.weekday % 7;
  const gridStart = startDate.minus({ days: leadingDays });
  const totalDays = Math.floor(endDate.diff(gridStart, 'days').days) + 1;

  const days: DayCell[] = Array.from({ length: totalDays }, (_, index) => {
    const date = gridStart.plus({ days: index });
    const dateKey = date.toISODate();
    const inRange = date >= startDate && date <= endDate;
    const count = inRange ? countsByDate.get(dateKey) || 0 : 0;
    return {
      date,
      dateKey,
      count,
      inRange,
    };
  });

  const inRangeDays = days.filter((day) => day.inRange);
  const maxCount = inRangeDays.reduce((max, day) => Math.max(max, day.count), 0);
  const totalCount = inRangeDays.reduce((sum, day) => sum + day.count, 0);

  const weeks: DayCell[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  const monthLabels = weeks.map((week, index) => {
    const firstInRange = week.find((day) => day.inRange);
    if (!firstInRange) {
      return { key: `month-${index}`, label: '' };
    }
    if (index === 0) {
      return { key: `month-${index}`, label: firstInRange.date.toFormat('LLL') };
    }
    const prevWeek = weeks[index - 1]?.find((day) => day.inRange);
    if (!prevWeek) {
      return { key: `month-${index}`, label: firstInRange.date.toFormat('LLL') };
    }
    return {
      key: `month-${index}`,
      label: firstInRange.date.month !== prevWeek.date.month ? firstInRange.date.toFormat('LLL') : '',
    };
  });

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <div className="font-semibold text-darkgray">Credit activity</div>
        <div className="text-mediumgray">{`${totalCount} credits in the last year`}</div>
      </div>
      <div className="mt-3 overflow-x-auto">
        <div className="inline-flex">
          <div className="flex flex-col gap-1 pr-2 pt-5 text-xs text-mediumgray">
            <div className="h-3" />
            <div className="h-3 flex items-center">Mon</div>
            <div className="h-3" />
            <div className="h-3 flex items-center">Wed</div>
            <div className="h-3" />
            <div className="h-3 flex items-center">Fri</div>
            <div className="h-3" />
          </div>
          <div>
            <div className="flex gap-1 text-xs text-mediumgray">
              {monthLabels.map((month) => (
                <div key={month.key} className="w-3">
                  {month.label}
                </div>
              ))}
            </div>
            <div className="mt-1 inline-flex gap-1">
              {weeks.map((week) => (
                <div key={`week-${week.map((day) => day.dateKey).join('-')}`} className="flex flex-col gap-1">
                  {week.map((day) => {
                    const level = getLevel(day.count, maxCount);
                    const title = day.inRange
                      ? `${day.count === 0 ? 'No' : day.count} credit${day.count === 1 ? '' : 's'} on ${day.date.toFormat('LLL d, yyyy')}`
                      : 'Outside of range';
                    return (
                      <MobileTooltip key={day.dateKey} title={title} arrow>
                        <div
                          className={[
                            'h-3 w-3 rounded-sm border border-lightgray transition-colors duration-200',
                            levelClasses[level],
                            day.inRange ? '' : 'opacity-40',
                          ].join(' ')}
                        />
                      </MobileTooltip>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-end gap-1 text-xs text-mediumgray">
        <span className="mr-1">Less</span>
        {levelClasses.map((levelClass) => (
          <div
            key={`legend-${levelClass}`}
            className={`h-3 w-3 rounded-sm border border-lightgray ${levelClass}`}
          />
        ))}
        <span className="ml-1">More</span>
      </div>
    </div>
  );
};
